import { Collection, Database } from '@nozbe/watermelondb';

import { ContactModel, ContactPayload } from '../../../../types/Contacts';
import { HorseContactModel } from '../../../../types/HorseContact';
import { LocationModel } from '../../../../types/Location';
import { DBWriterLoggerFunction } from '../../../../types/logger';
import { UserModel, UserPayload } from '../../../../types/User';
import { capitalizeWords, stripSpecialChars } from '../../../../utils/string';

export const prepareCreateContact = ({
    payload,
    userId,
    organisationID,
    locationElement,
    collection,
}: {
    payload: ContactPayload;
    userId: string;
    organisationID: string;
    locationElement?: LocationModel;
    collection: Collection<ContactModel>;
}) =>
    collection.prepareCreate((contact) => {
        contact.firstName = capitalizeWords(payload.firstName);
        contact.lastName = capitalizeWords(payload.lastName);
        // Description is not used for contacts, keep it empty for sync compatibility.
        contact.description = null;
        contact.role = payload.role;
        contact.phone = stripSpecialChars(payload.phone);
        contact.phonePrefix = payload.phonePrefix;
        contact.email = payload.email;
        contact.businessName = payload.businessName;
        contact.address = payload.address;
        contact.comments = payload.comments;
        contact.locationId = locationElement?.id
            ? locationElement.id
            : contact?.locationId;
        contact.billable = payload.billable || false;
        contact.hidden = payload.hidden || false;
        contact.userId = userId;
        contact.organisationId = organisationID;
    });

export const prepareCreateBatchContact = ({
    payload,
    userId,
    organisationID,
    locationElement,
    collection,
}: {
    payload: ContactPayload;
    userId: string;
    organisationID: string;
    locationElement?: LocationModel;
    collection: Collection<ContactModel>;
}) =>
    collection.prepareCreate((contact) => {
        contact.firstName = capitalizeWords(payload.firstName);
        contact.lastName = capitalizeWords(payload.lastName);
        contact.description = null;
        contact.role = payload.role || null;
        contact.phone = stripSpecialChars(payload?.phone);
        contact.phonePrefix = payload.phonePrefix;
        contact.email = payload.email;
        contact.address = payload.address;
        contact.comments = payload.comments || null;
        contact.locationId = locationElement?.id ?? null;
        contact.billable = payload.billable || false;
        contact.hidden = payload.hidden || false;
        contact.userId = userId;
        contact.organisationId = organisationID;
    });

export const prepareCreateHorseContacts = ({
    horses,
    contact,
    database,
}: {
    horses?: ContactPayload['horses'];
    contact: ContactModel;
    database: Database;
}): HorseContactModel[] => {
    if (!horses?.length) return [];

    const horseContactsCollection =
        database.collections.get<HorseContactModel>('horse_contacts');

    return horses.map((horse) =>
        horseContactsCollection.prepareCreate((horseContact) => {
            horseContact.contactId = contact.id;
            horseContact.userId = contact.userId;
            horseContact.organisationId = contact.organisationId;
            horseContact.horseId = horse.id;
        }),
    );
};

export const prepareContactBatchUserUpdate = ({
    user,
    userPayload,
}: {
    user: UserModel;
    userPayload: UserPayload;
}) =>
    user.prepareUpdate((userElement) => {
        Object.keys(userPayload).forEach((key) => {
            userElement[key] = userPayload[key];
        });
    });

export const logAddContactActions = ({
    contact,
    horseContactsToAdd,
    logDBAction,
    tableName,
}: {
    contact: ContactModel;
    horseContactsToAdd: HorseContactModel[];
    logDBAction: DBWriterLoggerFunction;
    tableName: string;
}) => {
    logDBAction({
        message: 'Create contact',
        modelName: tableName,
        payload: contact,
    });

    horseContactsToAdd.forEach((createdHorseContact) =>
        logDBAction({
            message: 'Create contact - create horse contact',
            modelName: tableName,
            payload: createdHorseContact,
        }),
    );
};

export const logBatchAddContactActions = ({
    locationsToAdd,
    horseContactsToAdd,
    contactsToAdd,
    userToUpdate,
    logDBAction,
    tableName,
}: {
    locationsToAdd: LocationModel[];
    horseContactsToAdd: HorseContactModel[];
    contactsToAdd: ContactModel[];
    userToUpdate?: UserModel;
    logDBAction: DBWriterLoggerFunction;
    tableName: string;
}) => {
    locationsToAdd.forEach((locationElement) =>
        logDBAction({
            message: 'Create batch contacts - create location',
            modelName: tableName,
            payload: locationElement,
        }),
    );
    horseContactsToAdd.forEach((createdHorseContact) =>
        logDBAction({
            message: 'Create batch contacts - create horse contact',
            modelName: tableName,
            payload: createdHorseContact,
        }),
    );
    logDBAction({
        message: 'Create batch contacts',
        modelName: tableName,
        payload: contactsToAdd,
    });

    if (userToUpdate) {
        logDBAction({
            message: 'Create batch contacts - update user',
            modelName: 'users',
            payload: userToUpdate,
        });
    }
};
